import axios from '../api';

export interface CustomerProfile {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  location?: {
    address?: string;
    city?: string;
    pincode?: string;
  };
  favorites: string[];
  totalOrders: number;
  totalSpent: number;
  avatar?: string;
  twoFactorEnabled?: boolean;
}

export interface CartItem {
  _id: string;
  surpriseBox: any;
  quantity: number;
  price: number;
  addedAt: string;
}

export interface Cart {
  _id: string;
  user: string;
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
}

// --- Standalone restaurant / menu-item ordering (independent of the
// surprise-box cart above) ---

export interface RestaurantSummary {
  _id: string;
  name: string;
  description: string;
  category: string;
  cuisineTags: string[];
  location: { address?: string; city?: string };
  images?: string[];
  logo?: string;
  rating: { average: number; count: number };
}

export interface MenuItemForOrder {
  _id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  images?: string[];
  status: 'active' | 'sold_out' | 'unavailable';
  business?: { _id: string; name: string } | string;
  inventory?: { totalQuantity: number; remainingQuantity: number };
}

class CustomerService {
  // Profile
  async getProfile(): Promise<CustomerProfile> {
    const response = await axios.get('/api/customer/profile');
    return response.data.user;
  }

  async updateProfile(data: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    pincode?: string;
  }): Promise<CustomerProfile> {
    const response = await axios.put('/api/customer/profile', data);
    return response.data.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await axios.put('/api/customer/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Orders
  async getOrders(page: number = 1, limit: number = 20) {
    const response = await axios.get('/api/customer/orders', {
      params: { page, limit },
    });
    return response.data;
  }

  // Favorites
  async getFavorites() {
    const response = await axios.get('/api/customer/favorites');
    return response.data.favorites;
  }

  async addToFavorites(boxId: string) {
    const response = await axios.post(`/api/customer/favorites/${boxId}`);
    return response.data;
  }

  async removeFromFavorites(boxId: string) {
    const response = await axios.delete(`/api/customer/favorites/${boxId}`);
    return response.data;
  }

  // Cart
  async getCart(): Promise<Cart> {
    const response = await axios.get('/api/customer/cart');
    return response.data.cart;
  }

  async addToCart(surpriseBoxId: string, quantity: number = 1): Promise<Cart> {
    const response = await axios.post('/api/customer/cart', {
      surpriseBoxId,
      quantity,
    });
    return response.data.cart;
  }

  async updateCartItem(itemId: string, quantity: number): Promise<Cart> {
    const response = await axios.put(`/api/customer/cart/${itemId}`, {
      quantity,
    });
    return response.data.cart;
  }

  async removeFromCart(itemId: string): Promise<Cart> {
    const response = await axios.delete(`/api/customer/cart/${itemId}`);
    return response.data.cart;
  }

  async clearCart(): Promise<Cart> {
    const response = await axios.delete('/api/customer/cart');
    return response.data.cart;
  }

  async createOrder(orderData: {
    items: Array<{ surpriseBox: string; quantity: number; price: number }>;
    paymentMethod: string;
    couponCode?: string;
    discount?: number;
    totalAmount: number;
  }): Promise<any> {
    const response = await axios.post('/api/customer/orders', orderData);
    return response.data.order;
  }

  async getOrderDetails(orderId: string): Promise<any> {
    const response = await axios.get(`/api/customer/orders/${orderId}`);
    return response.data.order;
  }

  // --- Standalone restaurant / menu-item ordering ---

  /** Verified, active restaurants a customer can browse and order from directly. */
  async getRestaurants(search?: string): Promise<RestaurantSummary[]> {
    const response = await axios.get('/api/businesses/browse', {
      params: search ? { search } : undefined,
    });
    return response.data;
  }

  async getRestaurant(businessId: string): Promise<RestaurantSummary> {
    const response = await axios.get(`/api/businesses/${businessId}`);
    return response.data;
  }

  /** One restaurant's regular menu (not its surprise boxes). */
  async getRestaurantMenu(businessId: string): Promise<MenuItemForOrder[]> {
    const response = await axios.get('/api/menu-items', { params: { business: businessId } });
    return Array.isArray(response.data) ? response.data : response.data.menuItems || [];
  }

  /**
   * Place an order for regular menu items only — no surprise box. The
   * backend already accepts `items: []` alongside `menuItems`, so this hits
   * the same `/api/customer/orders` endpoint as [createOrder].
   */
  async createMenuOrder(
    menuItems: Array<{ menuItem: string; quantity: number }>,
    paymentMethod: string
  ): Promise<any> {
    const response = await axios.post('/api/customer/orders', {
      items: [],
      menuItems,
      paymentMethod,
    });
    return response.data.order;
  }
}

const customerService = new CustomerService();
export default customerService;
